import { CircularProgress } from '@mui/material'
import { useEffect, useRef } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router-dom'
import { getProfileAsync } from '../../common/ProfileSlice'
import { DefaultDeviceVersion } from '../../data/Device'
import { IProcSave } from '../../data/ProcSave'
import { replaceHeader } from '../../utils/replaceHeader'
import { useAppDispatch, useAppSelector } from './RunReduxHooks'
import { saveProcedureAsync, saveProcedureToLocal } from './saveThunk'
import {
  getProcHeaderAndDataAsync,
  selectCurrentTime,
  selectDataLoaded,
  selectIsFinished,
  selectProcHeader,
  selectStartTime,
  selectStep,
  updateCurrentTime
} from './sliceRunProcHeader'
import { selectCyto } from './sliceCyto'
import { selectData } from './sliceTimeline'
import { resetForDeviceVersion } from './sliceSetup'

export default function Run() {
  const { partitionKey, rowKey } = useParams()
  const dispatch = useAppDispatch()
  const proc = useAppSelector(selectProcHeader)
  const dataLoaded = useAppSelector(selectDataLoaded)
  const navigate = useNavigate()
  const workflowStateStepIndex = useAppSelector(selectStep)
  const isFinished = useAppSelector(selectIsFinished)
  const timelineData = useAppSelector(selectData)
  const cytoData = useAppSelector(selectCyto)
  const currentTime = useAppSelector(selectCurrentTime)
  const startTime = useAppSelector(selectStartTime)
  const setupInitializedForProc = useRef<string | null>(null)

  useEffect(() => {
    replaceHeader('Wizard', `${window.location.origin}/runIcon.ico`)
  }, [])

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (dataLoaded && proc !== null && !isFinished && proc.rowKey) {
        const savePayload: IProcSave = {
          header: { ...proc },
          data: timelineData,
          cytoData
        }

        savePayload.header.currentTime = currentTime
        savePayload.header.startTime = startTime

        saveProcedureToLocal(proc.rowKey, savePayload, 'pending')
      }

      event.returnValue = 'Are you sure you want to leave? Your data may not be saved.'
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [dataLoaded, proc, isFinished, timelineData, cytoData, currentTime, startTime])

  useEffect(() => {
    const timerId = setInterval(() => {
      dispatch(updateCurrentTime(Date.now()))
    }, 1000)

    return () => clearInterval(timerId)
  }, [dispatch])

  useEffect(() => {
    if (proc !== null && dataLoaded && !isFinished) {
      dispatch(saveProcedureAsync())
    }
  }, [dispatch, dataLoaded, isFinished, proc, proc?.rowKey, proc?.step])

  useEffect(() => {
    const saveTimerId = setInterval(() => {
      if (dataLoaded && proc !== null && !isFinished) {
        dispatch(saveProcedureAsync())
      }
    }, 1 * 60 * 1000)

    return () => clearInterval(saveTimerId)
  }, [dispatch, dataLoaded, proc, isFinished])

  useEffect(() => {
    if (!partitionKey || !rowKey) {
      return
    }

    dispatch(getProcHeaderAndDataAsync({ partitionKey, rowKey }))
    dispatch(getProfileAsync())
  }, [dispatch, partitionKey, rowKey])

  useEffect(() => {
    if (proc === null || !dataLoaded || !proc.rowKey) {
      return
    }

    if (setupInitializedForProc.current === proc.rowKey) {
      return
    }

    dispatch(resetForDeviceVersion(proc.deviceVersion ?? DefaultDeviceVersion))
    setupInitializedForProc.current = proc.rowKey
  }, [dispatch, proc, proc?.rowKey, proc?.deviceVersion, dataLoaded])

  useEffect(() => {
    if (proc !== null) {
      if (isFinished) {
        navigate('finish')
      } else {
        if (dataLoaded) {
          navigate(`s/${workflowStateStepIndex}`)
        }
      }
    }
  }, [proc, workflowStateStepIndex, isFinished, dataLoaded, navigate])

  return (
    <>
      {partitionKey === undefined || rowKey === undefined
        ? 'Invalid parameters'
        : proc === null || !dataLoaded
          ? <CircularProgress size='36px' sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
          : <Outlet />
      }
    </>
  )
}